import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchEventImageActivities } from "../../../api/eventImages";
import { requestUploadUrl, createImageRecord } from "./photoApi";
import { stripExif } from "./exifStrip";
import "./PhotoUploadTab.css";

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_PARALLEL = 3;

const ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

const todayLocalISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const isHeic = (file) => {
  if (!file) return false;
  const t = (file.type || "").toLowerCase();
  if (t === "image/heic" || t === "image/heif") return true;
  const name = (file.name || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif");
};

const formatBytes = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

// Friendly label for each row state.
const STATUS_LABEL = {
  pending: "Pending",
  stripping: "Stripping EXIF…",
  uploading: "Uploading…",
  registering: "Saving record…",
  done: "Done",
  error: "Error",
  cancelled: "Cancelled",
  invalid: "Invalid",
};

const STATUS_PILL_CLASS = {
  pending: "photo-upload-tab__pill--pending",
  stripping: "photo-upload-tab__pill--inflight",
  uploading: "photo-upload-tab__pill--inflight",
  registering: "photo-upload-tab__pill--inflight",
  done: "photo-upload-tab__pill--done",
  error: "photo-upload-tab__pill--error",
  cancelled: "photo-upload-tab__pill--error",
  invalid: "photo-upload-tab__pill--error",
};

const IN_FLIGHT_STATES = new Set(["stripping", "uploading", "registering"]);

let nextRowId = 1;
const makeRowId = () => {
  const id = `row-${nextRowId}`;
  nextRowId += 1;
  return id;
};

// Build an indexed default display name. Files that share the same
// activity+date in the queue get a numeric suffix (_1, _2, …) so the keys
// are unique. Standalone files keep the un-suffixed form.
const computeDefaultDisplayName = ({ rowId, queue, activity, date }) => {
  const baseDate = (date || todayLocalISO()).slice(0, 10);
  const base = `${baseDate}_${activity || "untitled"}`;
  const peers = queue.filter(
    (r) => (r.activity || "") === (activity || "") && (r.takenDate || "").slice(0, 10) === baseDate
  );
  if (peers.length <= 1) return base;
  const idx = peers.findIndex((r) => r.id === rowId);
  return `${base}_${idx >= 0 ? idx + 1 : peers.length}`;
};

const PhotoUploadTab = () => {
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);

  // Shared form state — defaults applied to newly added rows.
  const [sharedActivity, setSharedActivity] = useState("");
  const [sharedDate, setSharedDate] = useState(todayLocalISO());

  // The queue is the source of truth for everything per-file.
  // Row shape: {
  //   id, file, previewUrl, displayName, displayNameDirty,
  //   activity, activityDirty, takenDate, takenDateDirty,
  //   exifTakenDate (string|null), strippedBlob (Blob|null),
  //   status, errorMessage, sizeBytes
  // }
  const [queue, setQueue] = useState([]);

  // Cancel flag — when true, pending rows transition to cancelled instead of
  // being picked up by the worker. In-flight rows continue to completion.
  const [cancelRequested, setCancelRequested] = useState(false);

  // Atomic counter (via ref) for the parallel-3 worker. We can't trust React
  // state for this because state updates are batched/async and the worker
  // loop runs synchronously across multiple promise resolutions.
  const inFlightCountRef = useRef(0);
  const workingRowsRef = useRef(new Set());
  const queueRef = useRef([]);
  const cancelRef = useRef(false);
  const fileInputRef = useRef(null);

  // Keep refs in sync with state for the worker loop.
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);
  useEffect(() => {
    cancelRef.current = cancelRequested;
  }, [cancelRequested]);

  // Fetch activities on mount.
  useEffect(() => {
    let cancelled = false;
    setActivitiesLoading(true);
    fetchEventImageActivities()
      .then((resp) => {
        if (cancelled) return;
        const acts = Array.isArray(resp?.activities) ? resp.activities : [];
        const sorted = [...acts].sort((a, b) =>
          String(a).localeCompare(String(b))
        );
        setActivities(sorted);
        if (sorted.length > 0) {
          setSharedActivity((prev) => prev || sorted[0]);
        }
        setActivitiesLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error("PhotoUploadTab activities error:", err);
        setActivitiesError(err);
        setActivitiesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Free object URLs on unmount.
  useEffect(() => {
    return () => {
      // eslint-disable-next-line no-restricted-syntax
      for (const r of queueRef.current) {
        if (r.previewUrl) {
          try {
            URL.revokeObjectURL(r.previewUrl);
          } catch (e) {
            /* noop */
          }
        }
      }
    };
  }, []);

  // -------- Mutating helpers --------

  // Update a single row by id (functional updater on its current state).
  const updateRow = useCallback((rowId, patch) => {
    setQueue((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const next = typeof patch === "function" ? patch(r) : { ...r, ...patch };
        return next;
      })
    );
  }, []);

  // Recompute default display names for rows that haven't been hand-edited.
  // Called whenever queue membership/activity/date change so the indexed
  // suffixes (_1, _2…) stay in sync.
  const reindexDisplayNames = useCallback((rows) => {
    return rows.map((row) => {
      if (row.displayNameDirty) return row;
      const newName = computeDefaultDisplayName({
        rowId: row.id,
        queue: rows,
        activity: row.activity,
        date: row.takenDate,
      });
      if (newName === row.displayName) return row;
      return { ...row, displayName: newName };
    });
  }, []);

  // -------- File picker --------

  const handleFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    // Reset the input so the same file can be re-picked later.
    if (e.target) e.target.value = "";
    if (picked.length === 0) return;

    const newRows = picked.map((file) => {
      const id = makeRowId();
      const heic = isHeic(file);
      const wrongType =
        !heic && !ACCEPTED_TYPES.includes((file.type || "").toLowerCase());
      let previewUrl = null;
      try {
        previewUrl = URL.createObjectURL(file);
      } catch (e2) {
        previewUrl = null;
      }
      const baseRow = {
        id,
        file,
        previewUrl,
        displayName: "",
        displayNameDirty: false,
        activity: sharedActivity || "",
        activityDirty: false,
        takenDate: sharedDate || todayLocalISO(),
        takenDateDirty: false,
        exifTakenDate: null,
        strippedBlob: null,
        status: "pending",
        errorMessage: "",
        sizeBytes: file.size,
      };
      if (heic) {
        return {
          ...baseRow,
          status: "invalid",
          errorMessage: "HEIC not supported, please convert first.",
        };
      }
      if (wrongType) {
        return {
          ...baseRow,
          status: "invalid",
          errorMessage: `Unsupported file type "${file.type || "unknown"}". Use JPEG, PNG, or WEBP.`,
        };
      }
      if (file.size > MAX_BYTES) {
        return {
          ...baseRow,
          status: "invalid",
          errorMessage: `File too large (${formatBytes(file.size)}). Max is 10 MB.`,
        };
      }
      return baseRow;
    });

    setQueue((prev) => {
      const merged = [...prev, ...newRows];
      // Initial pass to assign default display names.
      return reindexDisplayNames(merged);
    });

    // Pre-strip EXIF for valid rows so we can prefill takenDate from
    // DateTimeOriginal. Done outside of setQueue so we can await.
    newRows.forEach((row) => {
      if (row.status !== "pending") return;
      stripExif(row.file)
        .then(({ blob, takenAt }) => {
          let exifIso = null;
          if (takenAt && typeof takenAt === "string") {
            exifIso = takenAt.slice(0, 10);
          }
          setQueue((prev) =>
            reindexDisplayNames(
              prev.map((r) => {
                if (r.id !== row.id) return r;
                const next = {
                  ...r,
                  strippedBlob: blob,
                  sizeBytes: blob ? blob.size : r.sizeBytes,
                  exifTakenDate: exifIso,
                };
                // Only override takenDate if user hasn't touched it AND we got
                // an EXIF date.
                if (!r.takenDateDirty && exifIso) {
                  next.takenDate = exifIso;
                }
                return next;
              })
            )
          );
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("EXIF preprocess failed; will retry at upload.", err);
        });
    });
  };

  // -------- Shared dropdown handlers --------

  const handleSharedActivityChange = (e) => {
    const v = e.target.value;
    setSharedActivity(v);
    setQueue((prev) =>
      reindexDisplayNames(
        prev.map((r) => {
          if (r.activityDirty) return r;
          if (IN_FLIGHT_STATES.has(r.status) || r.status === "done") return r;
          return { ...r, activity: v };
        })
      )
    );
  };

  const handleSharedDateChange = (e) => {
    const v = e.target.value;
    setSharedDate(v);
    setQueue((prev) =>
      reindexDisplayNames(
        prev.map((r) => {
          if (r.takenDateDirty) return r;
          if (r.exifTakenDate) return r; // EXIF wins over shared default.
          if (IN_FLIGHT_STATES.has(r.status) || r.status === "done") return r;
          return { ...r, takenDate: v };
        })
      )
    );
  };

  // -------- Per-row inline edits --------

  const handleRowDisplayNameChange = (rowId, value) => {
    updateRow(rowId, { displayName: value, displayNameDirty: true });
  };

  const handleRowActivityChange = (rowId, value) => {
    setQueue((prev) =>
      reindexDisplayNames(
        prev.map((r) =>
          r.id === rowId ? { ...r, activity: value, activityDirty: true } : r
        )
      )
    );
  };

  const handleRowDateChange = (rowId, value) => {
    setQueue((prev) =>
      reindexDisplayNames(
        prev.map((r) =>
          r.id === rowId ? { ...r, takenDate: value, takenDateDirty: true } : r
        )
      )
    );
  };

  const handleRemoveRow = (rowId) => {
    setQueue((prev) => {
      const target = prev.find((r) => r.id === rowId);
      if (target && target.previewUrl) {
        try {
          URL.revokeObjectURL(target.previewUrl);
        } catch (e) {
          /* noop */
        }
      }
      return reindexDisplayNames(prev.filter((r) => r.id !== rowId));
    });
  };

  const handleClearCompleted = () => {
    setQueue((prev) => {
      const keep = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const r of prev) {
        if (
          r.status === "done" ||
          r.status === "error" ||
          r.status === "cancelled" ||
          r.status === "invalid"
        ) {
          if (r.previewUrl) {
            try {
              URL.revokeObjectURL(r.previewUrl);
            } catch (e) {
              /* noop */
            }
          }
        } else {
          keep.push(r);
        }
      }
      return reindexDisplayNames(keep);
    });
  };

  // -------- The worker loop (parallel-3) --------

  // processOne: executes the per-file pipeline. Marks the row done/error.
  // Returns a promise that resolves either way (never rejects).
  const processOne = useCallback(
    async (rowId) => {
      const start = queueRef.current.find((r) => r.id === rowId);
      if (!start) return;

      try {
        // Step 1 — stripping (skip if already done from pre-strip).
        let blob = start.strippedBlob;
        if (!blob) {
          updateRow(rowId, { status: "stripping", errorMessage: "" });
          try {
            const res = await stripExif(start.file);
            blob = res.blob;
            updateRow(rowId, (r) => ({
              ...r,
              strippedBlob: blob,
              sizeBytes: blob ? blob.size : r.sizeBytes,
              exifTakenDate:
                r.exifTakenDate ||
                (res.takenAt && typeof res.takenAt === "string"
                  ? res.takenAt.slice(0, 10)
                  : null),
              takenDate:
                !r.takenDateDirty && res.takenAt && typeof res.takenAt === "string"
                  ? res.takenAt.slice(0, 10)
                  : r.takenDate,
            }));
          } catch (err) {
            // Strip failed — fall back to original file (matches single-file behavior).
            blob = start.file;
            updateRow(rowId, { strippedBlob: blob });
          }
        }

        const sizeBytes = blob ? blob.size : start.file.size;
        const contentType = start.file.type || "image/jpeg";

        if (sizeBytes > MAX_BYTES) {
          updateRow(rowId, {
            status: "error",
            errorMessage: `File too large (${formatBytes(sizeBytes)}). Max is 10 MB.`,
          });
          return;
        }

        // Snapshot the latest row values for activity / displayName / takenDate
        // — they may have been edited while this was queued.
        const latest =
          queueRef.current.find((r) => r.id === rowId) || start;
        const trimmedName = (latest.displayName || "").trim();
        if (!trimmedName) {
          updateRow(rowId, {
            status: "error",
            errorMessage: "Display name is required.",
          });
          return;
        }
        if (!latest.activity) {
          updateRow(rowId, {
            status: "error",
            errorMessage: "Activity is required.",
          });
          return;
        }

        // Step 2 — request presigned URL.
        updateRow(rowId, { status: "uploading", errorMessage: "" });
        const { uploadUrl, s3Key } = await requestUploadUrl({
          filename: start.file.name,
          contentType,
          sizeBytes,
          activity: latest.activity,
          displayName: trimmedName,
        });

        // Step 3 — PUT to S3 (no Authorization header).
        const putResp = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: blob,
        });
        if (!putResp.ok) {
          throw new Error(`S3 upload failed (${putResp.status})`);
        }

        // Step 4 — register the record.
        updateRow(rowId, { status: "registering" });
        const takenAtIso = latest.takenDate
          ? `${latest.takenDate}T00:00:00`
          : undefined;
        await createImageRecord({
          s3Key,
          displayName: trimmedName,
          activity: latest.activity,
          sizeBytes,
          takenAt: takenAtIso,
        });

        updateRow(rowId, (r) => {
          // Free object URL once successfully uploaded.
          if (r.previewUrl) {
            try {
              URL.revokeObjectURL(r.previewUrl);
            } catch (e) {
              /* noop */
            }
          }
          return { ...r, status: "done", previewUrl: null, errorMessage: "" };
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Photo upload error:", err);
        updateRow(rowId, {
          status: "error",
          errorMessage: err && err.message ? err.message : "Upload failed.",
        });
      }
    },
    [updateRow]
  );

  // Pump: while we have capacity and there are pending rows, kick off more.
  // Uses inFlightCountRef + workingRowsRef to ensure parallel-3 cap is enforced
  // atomically across re-entrant calls (each completion calls pump again).
  const pump = useCallback(() => {
    while (inFlightCountRef.current < MAX_PARALLEL) {
      if (cancelRef.current) {
        // Mark all remaining pending rows cancelled, then stop.
        setQueue((prev) =>
          prev.map((r) =>
            r.status === "pending" ? { ...r, status: "cancelled" } : r
          )
        );
        return;
      }
      const next = queueRef.current.find(
        (r) => r.status === "pending" && !workingRowsRef.current.has(r.id)
      );
      if (!next) return;

      workingRowsRef.current.add(next.id);
      inFlightCountRef.current += 1;

      // Fire and forget. Each completion releases its slot and re-pumps.
      processOne(next.id).finally(() => {
        workingRowsRef.current.delete(next.id);
        inFlightCountRef.current = Math.max(0, inFlightCountRef.current - 1);
        // Defer the next pump tick so React state updates flush first;
        // otherwise queueRef.current may be stale on the immediate re-entry.
        Promise.resolve().then(() => {
          if (inFlightCountRef.current === 0 && cancelRef.current) {
            // All in-flight done after a cancel — release the cancel flag.
            setCancelRequested(false);
          }
          pump();
        });
      });
    }
  }, [processOne]);

  // -------- Buttons --------

  const pendingCount = useMemo(
    () => queue.filter((r) => r.status === "pending").length,
    [queue]
  );
  const inFlightCount = useMemo(
    () => queue.filter((r) => IN_FLIGHT_STATES.has(r.status)).length,
    [queue]
  );
  const doneCount = useMemo(
    () => queue.filter((r) => r.status === "done").length,
    [queue]
  );
  const errorCount = useMemo(
    () =>
      queue.filter(
        (r) =>
          r.status === "error" ||
          r.status === "cancelled" ||
          r.status === "invalid"
      ).length,
    [queue]
  );
  const hasCompleted = doneCount + errorCount > 0;
  const anyInFlight = inFlightCount > 0;

  const handleUploadAll = () => {
    if (pendingCount === 0 || anyInFlight) return;
    setCancelRequested(false);
    cancelRef.current = false;
    // pump reads from queueRef which is updated via the effect above.
    pump();
  };

  const handleCancel = () => {
    setCancelRequested(true);
    cancelRef.current = true;
    // Mark currently-pending rows as cancelled immediately. In-flight rows
    // continue to natural completion.
    setQueue((prev) =>
      prev.map((r) =>
        r.status === "pending" ? { ...r, status: "cancelled" } : r
      )
    );
  };

  // -------- Render --------

  const totalCount = queue.length;
  const summary = `${doneCount} done · ${errorCount} errors · ${pendingCount + inFlightCount} pending`;

  return (
    <div className="photo-upload-tab">
      <h2>Upload Photos</h2>
      <p className="photo-upload-tab__hint">
        JPEG, PNG, or WEBP. Max 10 MB per file. EXIF metadata is stripped from
        JPEGs client-side before upload. HEIC is not supported — convert first.
        Up to 3 files upload in parallel.
      </p>

      <div className="photo-upload-tab__field">
        <label htmlFor="photo-upload-file">Add Files</label>
        <input
          id="photo-upload-file"
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFilesPicked}
        />
        <div className="photo-upload-tab__file-info">
          You can select multiple files. Picking more files appends to the queue.
        </div>
      </div>

      <div className="photo-upload-tab__shared-row">
        <div className="photo-upload-tab__field">
          <label htmlFor="photo-upload-shared-activity">Default Activity</label>
          {activitiesLoading ? (
            <div className="photo-upload-tab__file-info">
              Loading activities…
            </div>
          ) : activitiesError ? (
            <div className="photo-upload-tab__status photo-upload-tab__status--error">
              Could not load activities. Try refreshing.
            </div>
          ) : (
            <select
              id="photo-upload-shared-activity"
              value={sharedActivity}
              onChange={handleSharedActivityChange}
            >
              {activities.length === 0 ? (
                <option value="">(no activities found)</option>
              ) : (
                activities.map((act) => (
                  <option key={act} value={act}>
                    {act}
                  </option>
                ))
              )}
            </select>
          )}
        </div>

        <div className="photo-upload-tab__field">
          <label htmlFor="photo-upload-shared-date">Default Taken Date</label>
          <input
            id="photo-upload-shared-date"
            type="date"
            value={sharedDate}
            onChange={handleSharedDateChange}
          />
          <div className="photo-upload-tab__file-info">
            Used as a fallback when EXIF DateTimeOriginal isn't present.
          </div>
        </div>
      </div>

      {totalCount > 0 && (
        <div className="photo-upload-tab__summary">
          <strong>{summary}</strong>
          <span className="photo-upload-tab__summary-total">
            {totalCount} file{totalCount === 1 ? "" : "s"} in queue
          </span>
        </div>
      )}

      {totalCount > 0 && (
        <div className="photo-upload-tab__queue-wrap">
          <table className="photo-upload-tab__queue">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Preview</th>
                <th>Filename</th>
                <th>Display Name</th>
                <th>Activity</th>
                <th>Taken Date</th>
                <th>Status</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => {
                const inFlight = IN_FLIGHT_STATES.has(row.status);
                const removable =
                  row.status === "pending" ||
                  row.status === "error" ||
                  row.status === "cancelled" ||
                  row.status === "invalid";
                const editable = !inFlight && row.status !== "done";
                return (
                  <tr key={row.id}>
                    <td>
                      {row.previewUrl ? (
                        <img
                          src={row.previewUrl}
                          alt=""
                          className="photo-upload-tab__thumb"
                        />
                      ) : (
                        <div className="photo-upload-tab__thumb photo-upload-tab__thumb--placeholder" />
                      )}
                    </td>
                    <td>
                      <div
                        className="photo-upload-tab__filename"
                        title={row.file.name}
                      >
                        {row.file.name}
                      </div>
                      <div className="photo-upload-tab__file-info">
                        {formatBytes(row.sizeBytes)}
                      </div>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="photo-upload-tab__name-input"
                        value={row.displayName}
                        disabled={!editable}
                        onChange={(e) =>
                          handleRowDisplayNameChange(row.id, e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <select
                        className="photo-upload-tab__activity-select"
                        value={row.activity}
                        disabled={
                          !editable ||
                          activitiesLoading ||
                          activities.length === 0
                        }
                        onChange={(e) =>
                          handleRowActivityChange(row.id, e.target.value)
                        }
                      >
                        {activities.length === 0 && (
                          <option value="">(none)</option>
                        )}
                        {activities.map((act) => (
                          <option key={act} value={act}>
                            {act}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="date"
                        className="photo-upload-tab__date-input"
                        value={row.takenDate}
                        disabled={!editable}
                        onChange={(e) =>
                          handleRowDateChange(row.id, e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <span
                        className={`photo-upload-tab__pill ${STATUS_PILL_CLASS[row.status] || ""}`}
                      >
                        {STATUS_LABEL[row.status] || row.status}
                      </span>
                      {row.errorMessage && (
                        <div className="photo-upload-tab__inline-error">
                          {row.errorMessage}
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="photo-upload-tab__btn photo-upload-tab__btn--danger"
                        onClick={() => handleRemoveRow(row.id)}
                        disabled={!removable}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="photo-upload-tab__actions">
        <button
          type="button"
          className="photo-upload-tab__submit"
          onClick={handleUploadAll}
          disabled={pendingCount === 0 || anyInFlight}
        >
          {anyInFlight
            ? `Uploading… (${inFlightCount} in flight)`
            : `Upload All (${pendingCount})`}
        </button>
        {anyInFlight && (
          <button
            type="button"
            className="photo-upload-tab__reset"
            onClick={handleCancel}
            disabled={cancelRequested}
          >
            {cancelRequested ? "Cancelling…" : "Cancel Pending"}
          </button>
        )}
        {hasCompleted && !anyInFlight && (
          <button
            type="button"
            className="photo-upload-tab__reset"
            onClick={handleClearCompleted}
          >
            Clear Completed
          </button>
        )}
      </div>
    </div>
  );
};

export default PhotoUploadTab;
